import { ComponentMapIds } from 'src/components/map';
// import { SessionFilterDefaults } from 'components/SessionFilterDefaults';
import { IView } from 'Framework';
import { ViewMode, CustMode, Vehicles } from 'src/enums';

export interface ILeftPaneControl {
    isChangedFlag: boolean;
    tabName?: ComponentMapIds;
    /** This can be the name or index of the subtab, usually a string that can be parsed if needed. */
    subtab?: string | number;
    [name: string]: any;
}

/** Interfaces */
export interface IFrameworkState {
    isLoggedIn: boolean;
    hasSpecs: boolean;
    initialLoadDone: boolean;
    isLoggedOut: boolean;
    error: string;
    locale: string;
    theme: string;
    timeoutTimer: number;
    userActiveFlag: boolean;
    rtsToken: string;
    mainPaneViewOrder: IView[];
    leftPaneViewOrder: IView[];
    viewMode: ViewMode;
    hasRunArea: boolean;
    specProfile: string;
    custMode: CustMode;
    store: string;
    branch: string;
    loadNameId: string;
    loadVin: string;
    loadAppt: string;
    loadRO: string;
    loadOpCode: string;
    previewFlag: string;
    tokenErrMsg: string;
    oemFirstName: string;
    oemLastName: string;
    oemEmail: string;
    oemPhone: string;
    oemError: string;
    addlTrackToken: string;
    addlTrackVin: string;
    addlTrackYear: string;
    addlTrackMake: string;
    addlTrackModel: string;
    resetPassFlag: string;
    toyLexStartSent: boolean;
    // toyLexApptSent: boolean;
    toyLexLoginSent: boolean;
    toyLexAccCrtSent: boolean;
    newTabUrl: string;
    serviceDocsFlag: string;
    waitOnRecall: boolean;
    noAutoRecall: boolean;
    eraError: string;
    desktopOrMobile: string;
    previousToken: string;
    dealerCountry: string;
    dealerLangCode: string;
    sysId: string;
    goToLoginFlag: string;
    checkRepairStatusFlag: string;
    viewServiceHistFlag: string;
    profileOpen: boolean;
    idmDocs: string;
    idmFile: string;
    idmFiles: string[];
    osdWebViewFlag: string;
    portalLaunched: string;
    osdLaunched: string;
    eApprovalLaunched: string;
    smsLaunched: string;
    tlmId: string;
    stationNum: string;
    taskId: string;
    docType: string;
    rcardAvailFlag: string;
    isCookiesEnabled: boolean;
    sessionId: string;
    logId: string;
    miscdec: string;
    waitOnMC: boolean;
    toyotaMCLaunched: string;
    lexusMCLaunched: string;
    toyotaSSC2Launched: string;
    lexusSSC2Launched: string;
    toyotaCustSearchLaunched: string;
    lexusCustSearchLaunched: string;
    viewVehicles: Vehicles;
    vinDialogOpen: boolean;
    ImageServerType: string | undefined;
    gmossVersion: string;
    tealiumAccount: string;
    toyotaTDMCLaunched: string;
    lexusTDMCLaunched: string;
    toyotaTDSSC2Launched: string;
    lexusTDSSC2Launched: string;
    toyotaTDCSLaunched: string;
    lexusTDCSLaunched: string;
    vehSelReset: boolean;
    idkMileage: string;
    vwDeepLinkMileage: string;
    vwVariant: string;
    vwProvider: string;
    vwKeyword: string;
    vehicleAddTriggered: boolean;
}

/** Initial State */
const initialState: IFrameworkState = {
    isLoggedIn: false,
    hasSpecs: false,
    initialLoadDone: false,
    isLoggedOut: true,
    error: '',
    locale: 'en-US',
    theme: 'normal',
    timeoutTimer: 60,
    userActiveFlag: true,
    rtsToken: '',
    mainPaneViewOrder: [],
    leftPaneViewOrder: [],
    viewMode: ViewMode.LandingPage,
    hasRunArea: false,
    specProfile: '',
    custMode: CustMode.Nothing,
    store: '',
    branch: '',
    loadNameId: '',
    loadVin: '',
    loadAppt: '',
    loadRO: '',
    loadOpCode: '',
    previewFlag: '',
    tokenErrMsg: '',
    oemFirstName: '',
    oemLastName: '',
    oemEmail: '',
    oemPhone: '',
    oemError: '',
    addlTrackToken: '',
    addlTrackVin: '',
    addlTrackYear: '',
    addlTrackMake: '',
    addlTrackModel: '',
    resetPassFlag: '',
    toyLexStartSent: false,
    // toyLexApptSent: false,
    toyLexLoginSent: false,
    toyLexAccCrtSent: false,
    newTabUrl: '',
    serviceDocsFlag: '',
    waitOnRecall: false,
    noAutoRecall: false,
    eraError: '',
    desktopOrMobile: '',
    previousToken: '',
    dealerCountry: 'US',
    dealerLangCode: 'EN',
    sysId: '',
    goToLoginFlag: '',
    checkRepairStatusFlag: '',
    viewServiceHistFlag: '',
    profileOpen: false,
    idmDocs: '',
    idmFile: '',
    idmFiles: [],
    osdWebViewFlag: '',
    portalLaunched: '',
    osdLaunched: '',
    eApprovalLaunched: '',
    smsLaunched: '',
    tlmId: '',
    stationNum: '',
    taskId: '',
    docType: '',
    rcardAvailFlag: '',
    isCookiesEnabled: navigator.cookieEnabled,
    sessionId: '',
    logId: '',
    miscdec: '',
    waitOnMC: false,
    toyotaMCLaunched: '',
    lexusMCLaunched: '',
    toyotaSSC2Launched: '',
    lexusSSC2Launched: '',
    toyotaCustSearchLaunched: '',
    lexusCustSearchLaunched: '',
    viewVehicles: Vehicles.All,
    vinDialogOpen: false,
    ImageServerType: undefined,
    gmossVersion: '',
    tealiumAccount: '',
    toyotaTDMCLaunched: '',
    lexusTDMCLaunched: '',
    toyotaTDSSC2Launched: '',
    lexusTDSSC2Launched: '',
    toyotaTDCSLaunched: '',
    lexusTDCSLaunched: '',
    vehSelReset: false,
    idkMileage: '',
    vwDeepLinkMileage: '',
    vwVariant: '',
    vwProvider: '',
    vwKeyword: '',
    vehicleAddTriggered: false,
};

// Valid actions for this reducer.
interface IActionDef1 {
    type: 'Framework/RECEIVE_LOGIN'
    | 'Framework/REQUEST_LOGIN'
    | 'Framework/TRIGGER_NEXTSTATUS'
    | 'Framework/SPECS_RECEIVED'
    | 'Framework/INITIAL_LOAD_DONE'
    | 'LOGOUT'
    | 'Framework/CLOSE_GLOBAL_PROMPT'
    | 'Framework/CLEAR_RIGHT_PANES'
    | 'Framework/CLEAR_LEFT_PANES'
    | 'Framework/CLEAR_MAIN_PANES'
    | 'Framework/Reset_HasSpecs';
}
interface IActionDef2 {
    type: 'Framework/SET_LOCALE'
    | 'Framework/SET_THEME'
    | 'Framework/SET_DBVER'
    | 'Framework/DISPLAY_GLOBAL_PROMPT';
    payload: string;
}
interface IActionDef3 {
    type: 'Framework/TRIGGER_USERACTIVE' | 'Framework/SET_USER_CAN_SEND_TEXT';
    payload: boolean;
}
interface IActionDef4 {
    type: 'Framework/SET_AUTOLOGOFFTIME' | 'Framework/REMOVE_NOTIFICATION' | 'Framework/CHANGE_CLIENTPROFILE_PROSPECT';
    payload: number;
}
interface IActionDef5 {
    type: 'Framework/ERROR';
    error: string;
}
interface IActionSetScreenFilters {
    type: 'Framework/SET_SESSIONFILTERS';
    payload: Record<string, Record<string, any>>;
}
interface IActionChangeLeftPaneControl {
    type: 'Framework/CHANGE_LEFTPANE_CONTROL';
    payload: ILeftPaneControl;
}
interface IActionPushToPane {
    type: 'Framework/PUSH_TO_LEFT_PANE'
    | 'Framework/PUSH_TO_RIGHT_PANE'
    | 'Framework/PUSH_TO_TOP_LEFT_PANE'
    | 'Framework/PUSH_TO_TOP_RIGHT_PANE'
    | 'Framework/DOCK_TO_RIGHT_PANE'
    | 'Framework/DOCK_TO_TOP_RIGHT_PANE'
    | 'Framework/DOCK_TO_LEFT_PANE'
    | 'Framework/DOCK_TO_TOP_LEFT_PANE'
    | 'Framework/PUSH_TO_MAIN_PANE'
    | 'Framework/DOCK_TO_MAIN_PANE';
    payload: IView;
}
interface IActionPopFromPane {
    type: 'Framework/POP_FROM_LEFT_PANE'
    | 'Framework/POP_FROM_RIGHT_PANE'
    | 'Framework/POP_FROM_MAIN_PANE';
    payload: string[];
}
interface IActionSaveCaptcha {
    type: 'Framework/SAVE_CAPTCHA';
    payload: { encryptedWord: string, image: number[] };
}
interface IActionSetCaptchaOpen {
    type: 'Framework/SET_CAPTCHA_OPEN';
    payload: boolean;
}
interface IActionSetCaptchaSuccessCallback {
    type: 'Framework/SET_CAPTCHA_SUCCESS_CALLBACK';
    payload: () => void;
}
interface IActionSetFullScreenView {
    type: 'Framework/SET_FULL_SCREEN_VIEW';
    payload: boolean;
}
interface IActionSetPrintView {
    type: 'Framework/SET_PRINT_VIEW';
    payload: { printView: boolean, printTitle?: string };
}

interface IActionViewMode {
    type: 'Framework/Set_ViewMode';
    payload: ViewMode;
}

interface IActionCustMode {
    type: 'Framework/Set_CustMode';
    payload: CustMode;
}

interface IActionSetRunArea {
    type: 'Framework/SET_RUN_AREA';
    payload: boolean;
}

interface IActionGetTokenData {
    type: 'Framework/GET_TOKEN_DATA';
    payload: IFrameworkState;
}

interface IActionClearTokenData {
    type: 'Framework/CLEAR_TOKEN_DATA';
    payload: IFrameworkState;
}

interface IActionSetPreviewFlag {
    type: 'Framework/SET_PREVIEW_FLAG';
    payload: string;
}

interface IActionAddLogEntry {
    type: 'Framework/ADD_LOG_ENTRY';
}

interface IActionGetAddlTrackCustomerInfo {
    type: 'Framework/GET_ADDL_TRACK_CUSTOMER_INFO';
    payload: IFrameworkState;
}

interface IActionSetAddlTrackData {
    type: 'Framework/SET_ADDL_TRACK_DATA';
    payload: IFrameworkState;
}

interface IActionClearAddlTrackCustInfo {
    type: 'Framework/CLEAR_ADDL_TRACK_CUST_INFO';
    payload: IFrameworkState;
}

interface IActionSetToyLexStart {
    type: 'Framework/SET_TOYLEX_START';
    payload: IFrameworkState;
}

// interface IActionSetToyLexAppt {
//     type: 'Framework/SET_TOYLEX_APPT';
//     payload: IFrameworkState;
// }

interface IActionSetToyLexLogin {
    type: 'Framework/SET_TOYLEX_LOGIN';
    payload: IFrameworkState;
}

interface IActionSetToyLexAccCrt {
    type: 'Framework/SET_TOYLEX_ACCT_CREATE';
    payload: IFrameworkState;
}

interface IActionGenerateUrl {
    type: 'Framework/GENERATE_URL';
    payload: string;
}

interface IActionGenerateDocsUrl {
    type: 'Framework/GENERATE_DOCS_URL';
    payload: string;
}

interface IActionSetWaitOnRecall {
    type: 'Framework/SET_WAIT_ON_RECALL';
    payload: boolean;
}
interface IActionSetNoAutoRecall {
    type: 'Framework/SET_NO_AUTO_RECALL';
    payload: boolean;
}


interface IActionSetERAError {
    type: 'Framework/ERA_ERROR';
    payload: string;
}

interface IActionSetDesktopOrMobile {
    type: 'Framework/SET_DESKTOP_OR_MOBILE'
    payload: string;
}

interface IActionSetPreviousToken {
    type: 'Framework/SET_PREVIOUS_TOKEN'
    payload: string;
}

interface IActionClearNewTabUrl {
    type: 'Framework/CLEAR_NEW_TAB_URL'
    payload: string;
}

interface IActionSetProfileOpen {
    type: 'Framework/SET_PROFILE_OPEN'
    payload: boolean;
}

interface IActionIDM {
    type: 'Framework/FIND_IDM_DOCS'
    | 'Framework/GET_IDM_DOC'
    | 'Framework/CLEAR_IDM_FILE'
    payload: string;
}

interface IActionIDMs {
    type: | 'Framework/GET_IDM_DOCS'
    payload: string[];
}

interface IActionAddCUC {
    type: 'Framework/ADD_CUC'
}

interface IActionSetWaitOnMC {
    type: 'Framework/SET_WAIT_ON_MC';
    payload: boolean;
}

interface IActionViewVehicles {
    type: 'Framework/VIEW_VEHICLES'
    payload: Vehicles;
}

interface IActionSetVinDialog {
    type: 'Framework/SET_VIN_DIALOG_OPEN'
    payload: boolean;
}

interface IActionSetVehSelReset {
    type: 'Framework/SET_VEHSEL_RESET';
    payload: boolean;
}

interface IActionSetIdkMileage {
    type: 'Framework/SET_IDK_MILEAGE';
    payload: string;
}

interface IActionSetVwDeepLinkMileage {
    type: 'Framework/SET_VW_DEEP_LINK_MILEAGE';
    payload: string;
}

interface IActionSetVwDeepLinkTrackData {
    type: 'Framework/SET_VW_DEEP_LINK_TRACK_DATA';
    payload: IFrameworkState;
}

interface IActionSetAddVehicle {
    type: 'Framework/SET_ADD_VEHICLE';
    payload: boolean;
}

export type ActionFramework = IActionDef1
    | IActionDef2
    | IActionDef3
    | IActionDef4
    | IActionDef5
    | IActionSetScreenFilters
    | IActionPushToPane
    | IActionPopFromPane
    | IActionChangeLeftPaneControl
    | IActionSaveCaptcha
    | IActionSetCaptchaOpen
    | IActionSetCaptchaSuccessCallback
    | IActionSetFullScreenView
    | IActionSetPrintView
    | IActionViewMode
    | IActionCustMode
    | IActionSetRunArea
    | IActionGetTokenData
    | IActionClearTokenData
    | IActionSetPreviewFlag
    | IActionAddLogEntry
    | IActionGetAddlTrackCustomerInfo
    | IActionSetAddlTrackData
    | IActionClearAddlTrackCustInfo
    | IActionSetToyLexStart
    // | IActionSetToyLexAppt
    | IActionSetToyLexLogin
    | IActionSetToyLexAccCrt
    | IActionGenerateUrl
    | IActionGenerateDocsUrl
    | IActionSetWaitOnRecall
    | IActionSetNoAutoRecall
    | IActionSetERAError
    | IActionSetDesktopOrMobile
    | IActionSetPreviousToken
    | IActionClearNewTabUrl
    | IActionSetProfileOpen
    | IActionIDM
    | IActionIDMs
    | IActionAddCUC
    | IActionSetWaitOnMC
    | IActionViewVehicles
    | IActionSetVinDialog
    | IActionSetVehSelReset
    | IActionSetIdkMileage
    | IActionSetVwDeepLinkMileage
    | IActionSetVwDeepLinkTrackData
    | IActionSetAddVehicle;

/** Reducer */
export function reducer(state = initialState, action: ActionFramework): IFrameworkState {
    switch (action.type) {
        case 'Framework/REQUEST_LOGIN':
            return {
                ...state,
                initialLoadDone: false,
                isLoggedOut: false,
            };
        case 'Framework/RECEIVE_LOGIN':
            return {
                ...state,
                isLoggedIn: true,
                userActiveFlag: true,
                error: '',
            };
        case 'Framework/TRIGGER_USERACTIVE':
            return {
                ...state,
                userActiveFlag: action.payload,
                timeoutTimer: 60,
            };

        case 'Framework/Set_ViewMode':
            return {
                ...state,
                viewMode: action.payload,
            };
        case 'Framework/Set_CustMode':
            return {
                ...state,
                custMode: action.payload,
            };
        case 'Framework/SET_RUN_AREA':
            return {
                ...state,
                initialLoadDone: true,
                hasRunArea: action.payload,
            };

        case 'Framework/SET_LOCALE':
            return {
                ...state,
                locale: action.payload,
            };
        case 'Framework/SET_THEME':
            return {
                ...state,
                theme: action.payload,
            };
        case 'Framework/SET_AUTOLOGOFFTIME':
            return {
                ...state,
                timeoutTimer: action.payload,
            };
        // case 'Framework/SET_SESSIONFILTERS':
        //     return {
        //         ...state,
        //         sessionFilters: action.payload,
        //     };
        case 'LOGOUT':
            return initialState;
        case 'Framework/ERROR':
            return {
                ...state,
                error: action.error,
            };

        case 'Framework/INITIAL_LOAD_DONE':
            return {
                ...state,
                initialLoadDone: true,
            };
        case 'Framework/PUSH_TO_MAIN_PANE':
            return {
                ...state,
                mainPaneViewOrder: [...state.mainPaneViewOrder, action.payload],
            };
        case 'Framework/POP_FROM_MAIN_PANE':
            return {
                ...state,
                mainPaneViewOrder: state.mainPaneViewOrder.filter((view: IView) => !action.payload.includes(view.viewId)),
            };
        case 'Framework/CLEAR_MAIN_PANES':
            return {
                ...state,
                mainPaneViewOrder: [],
            };
        case 'Framework/PUSH_TO_LEFT_PANE':
            return {
                ...state,
                leftPaneViewOrder: [...state.leftPaneViewOrder, action.payload],
            };
        case 'Framework/POP_FROM_LEFT_PANE':
            return {
                ...state,
                leftPaneViewOrder: state.leftPaneViewOrder.filter((view: IView) => !action.payload.includes(view.viewId)),
            };
        case 'Framework/CLEAR_LEFT_PANES':
            return {
                ...state,
                leftPaneViewOrder: [],
            };
        case 'Framework/GET_TOKEN_DATA':
            return {
                ...state,
                specProfile: action.payload.specProfile,
                store: action.payload.store,
                branch: action.payload.branch,
                tokenErrMsg: action.payload.tokenErrMsg,
                loadAppt: action.payload.loadAppt,
                loadNameId: action.payload.loadNameId,
                loadRO: action.payload.loadRO,
                loadVin: action.payload.loadVin,
                loadOpCode: action.payload.loadOpCode,
                resetPassFlag: action.payload.resetPassFlag,
                dealerCountry: action.payload.dealerCountry,
                dealerLangCode: action.payload.dealerLangCode,
                sysId: action.payload.sysId,
                serviceDocsFlag: action.payload.serviceDocsFlag,
                goToLoginFlag: action.payload.goToLoginFlag,
                checkRepairStatusFlag: action.payload.checkRepairStatusFlag,
                viewServiceHistFlag: action.payload.viewServiceHistFlag,
                osdWebViewFlag: action.payload.osdWebViewFlag,
                portalLaunched: action.payload.portalLaunched,
                osdLaunched: action.payload.osdLaunched,
                eApprovalLaunched: action.payload.eApprovalLaunched,
                smsLaunched: action.payload.smsLaunched,
                tlmId: action.payload.tlmId,
                stationNum: action.payload.stationNum,
                taskId: action.payload.taskId,
                docType: action.payload.docType,
                rcardAvailFlag: action.payload.rcardAvailFlag,
                sessionId: action.payload.sessionId,
                logId: action.payload.logId,
                miscdec: action.payload.miscdec,
                error: action.payload.error,
                toyotaMCLaunched: action.payload.toyotaMCLaunched ?? '',
                lexusMCLaunched: action.payload.lexusMCLaunched ?? '',
                toyotaSSC2Launched: action.payload.toyotaSSC2Launched ?? '',
                lexusSSC2Launched: action.payload.lexusSSC2Launched ?? '',
                toyotaCustSearchLaunched: action.payload.toyotaCustSearchLaunched ?? '',
                lexusCustSearchLaunched: action.payload.lexusCustSearchLaunched ?? '',
                ImageServerType: action.payload.ImageServerType ?? undefined,
                gmossVersion: action.payload.gmossVersion ?? '',
                tealiumAccount: action.payload.tealiumAccount,
                toyotaTDMCLaunched: action.payload.toyotaTDMCLaunched ?? '',
                lexusTDMCLaunched: action.payload.lexusTDMCLaunched ?? '',
                toyotaTDSSC2Launched: action.payload.toyotaTDSSC2Launched ?? '',
                lexusTDSSC2Launched: action.payload.lexusTDSSC2Launched ?? '',
                toyotaTDCSLaunched: action.payload.toyotaTDCSLaunched ?? '',
                lexusTDCSLaunched: action.payload.lexusTDCSLaunched ?? '',
            };
        case 'Framework/SET_PREVIEW_FLAG':
            return {
                ...state,
                previewFlag: action.payload,
            };
        case 'Framework/ADD_LOG_ENTRY':
            return {
                ...state,
            };
        case 'Framework/GET_ADDL_TRACK_CUSTOMER_INFO':
            return {
                ...state,
                oemFirstName: action.payload.oemFirstName,
                oemLastName: action.payload.oemLastName,
                oemEmail: action.payload.oemEmail,
                oemPhone: action.payload.oemPhone,
                oemError: action.payload.oemError,
                error: action.payload.error,
            };
        case 'Framework/SET_ADDL_TRACK_DATA':
            return {
                ...state,
                addlTrackToken: action.payload.addlTrackToken,
                addlTrackVin: action.payload.addlTrackVin,
                addlTrackYear: action.payload.addlTrackYear,
                addlTrackMake: action.payload.addlTrackMake,
                addlTrackModel: action.payload.addlTrackModel,
            };

        case 'Framework/CLEAR_ADDL_TRACK_CUST_INFO':
            return {
                ...state,
                oemFirstName: '',
                oemLastName: '',
                oemEmail: '',
                oemPhone: '',
                oemError: '',
                error: '',
            };

        case 'Framework/CLEAR_TOKEN_DATA':
            return {
                ...state,
                loadAppt: '',
                loadNameId: '',
                loadRO: '',
                loadVin: '',
                loadOpCode: '',
                resetPassFlag: '',
                miscdec: '',
            };
        case 'Framework/SET_TOYLEX_START':
            return {
                ...state,
                toyLexStartSent: action.payload.toyLexStartSent,
            };
        // case 'Framework/SET_TOYLEX_APPT':
        //     return {
        //         ...state,
        //         toyLexApptSent: action.payload.toyLexApptSent,
        //     };
        case 'Framework/SET_TOYLEX_LOGIN':
            return {
                ...state,
                toyLexLoginSent: action.payload.toyLexLoginSent,
            };
        case 'Framework/SET_TOYLEX_ACCT_CREATE':
            return {
                ...state,
                toyLexAccCrtSent: action.payload.toyLexAccCrtSent,
            };
        case 'Framework/GENERATE_URL':
            return {
                ...state,
                newTabUrl: action.payload,
            };
        case 'Framework/GENERATE_DOCS_URL':
            return {
                ...state,
                newTabUrl: action.payload,
            };
        case 'Framework/SET_WAIT_ON_RECALL':
            return {
                ...state,
                waitOnRecall: action.payload,
            };
        case 'Framework/SET_NO_AUTO_RECALL':
            return {
                ...state,
                noAutoRecall: action.payload,
            };
        case 'Framework/ERA_ERROR':
            return {
                ...state,
                eraError: action.payload,
            };
        case 'Framework/SET_DESKTOP_OR_MOBILE':
            return {
                ...state,
                desktopOrMobile: action.payload,
            };
        case 'Framework/SET_PREVIOUS_TOKEN':
            return {
                ...state,
                previousToken: action.payload,
            };
        case 'Framework/CLEAR_NEW_TAB_URL':
            return {
                ...state,
                newTabUrl: '',
            };
        case 'Framework/SET_PROFILE_OPEN':
            return {
                ...state,
                profileOpen: action.payload,
            };
        case 'Framework/FIND_IDM_DOCS':
            return {
                ...state,
                idmDocs: action.payload,
            };
        case 'Framework/GET_IDM_DOC':
            return {
                ...state,
                idmFile: action.payload,
            };
        case 'Framework/GET_IDM_DOCS':
            return {
                ...state,
                idmFiles: action.payload,
            };
        case 'Framework/CLEAR_IDM_FILE':
            return {
                ...state,
                idmFile: action.payload,
            };
        case 'Framework/ADD_CUC':
            return {
                ...state,
            };
        case 'Framework/SET_WAIT_ON_MC':
            return {
                ...state,
                waitOnMC: action.payload,
            };
        case 'Framework/VIEW_VEHICLES':
            return {
                ...state,
                viewVehicles: action.payload,
            };
        case 'Framework/SET_VIN_DIALOG_OPEN':
            return {
                ...state,
                vinDialogOpen: action.payload,
            };
        case 'Framework/SET_VEHSEL_RESET':
            return {
                ...state,
                vehSelReset: action.payload,
            };
        case 'Framework/SET_IDK_MILEAGE':
            return {
                ...state,
                idkMileage: action.payload,
            };
        case 'Framework/SET_VW_DEEP_LINK_MILEAGE':
            return {
                ...state,
                vwDeepLinkMileage: action.payload,
            };
        case 'Framework/SET_VW_DEEP_LINK_TRACK_DATA':
            return {
                ...state,
                vwVariant: action.payload.vwVariant,
                vwProvider: action.payload.vwProvider,
                vwKeyword: action.payload.vwKeyword,
            };
        case 'Framework/SET_ADD_VEHICLE':
            return {
                ...state,
                vehicleAddTriggered: action.payload,

            };
        default:
            return state;
    }
}
